import { useEffect, useRef, useState } from "react";
import { Pen, MoveUpLeft, Type, Undo2, Loader2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const COLORS = ["#D7262E", "#F59E0B", "#FFFFFF", "#22D3EE"];
const TOOLS = [
  { id: "pen", icon: Pen, label: "قلم" },
  { id: "arrow", icon: MoveUpLeft, label: "سهم" },
  { id: "text", icon: Type, label: "نص" },
];

function drawArrow(ctx, [x1, y1], [x2, y2], w) {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const h = w * 5;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2 - h * Math.cos(a - 0.45), y2 - h * Math.sin(a - 0.45));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - h * Math.cos(a + 0.45), y2 - h * Math.sin(a + 0.45));
  ctx.stroke();
}

function render(ctx, img, shapes) {
  ctx.drawImage(img, 0, 0);
  const w = Math.max(3, img.width / 250);
  shapes.forEach((s) => {
    ctx.strokeStyle = ctx.fillStyle = s.color;
    ctx.lineWidth = w;
    ctx.lineCap = ctx.lineJoin = "round";
    if (s.type === "pen") {
      ctx.beginPath();
      s.points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.stroke();
    } else if (s.type === "arrow") drawArrow(ctx, s.points[0], s.points[s.points.length - 1], w);
    else {
      ctx.font = `bold ${w * 9}px Cairo, sans-serif`;
      ctx.lineWidth = w * 1.4;
      ctx.strokeStyle = "rgba(0,0,0,0.75)";
      ctx.strokeText(s.text, ...s.points[0]);
      ctx.fillText(s.text, ...s.points[0]);
    }
  });
}

export const PhotoAnnotator = ({ open, onClose, inspectionId, photo, onSaved }) => {
  const canvas = useRef(null);
  const imgRef = useRef(null);
  const active = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !photo) return;
    setShapes([]);
    setLoading(true);
    let url;
    api.get(`/files/${photo.path}`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      const img = new Image();
      img.onload = () => {
        imgRef.current = img;
        const c = canvas.current;
        c.width = img.width;
        c.height = img.height;
        render(c.getContext("2d"), img, []);
        setLoading(false);
      };
      img.src = url;
    });
    return () => url && URL.revokeObjectURL(url);
  }, [open, photo]);

  useEffect(() => {
    if (imgRef.current && canvas.current) render(canvas.current.getContext("2d"), imgRef.current, shapes);
  }, [shapes]);

  const pt = (e) => {
    const r = canvas.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * canvas.current.width, ((e.clientY - r.top) / r.height) * canvas.current.height];
  };

  const down = (e) => {
    if (tool === "text") {
      if (text.trim()) setShapes((s) => [...s, { type: "text", color, text, points: [pt(e)] }]);
      return;
    }
    active.current = { type: tool, color, points: [pt(e)] };
    setShapes((s) => [...s, active.current]);
  };
  const move = (e) => {
    if (!active.current) return;
    active.current.points.push(pt(e));
    setShapes((s) => [...s.slice(0, -1), { ...active.current }]);
  };

  const save = async () => {
    setSaving(true);
    const blob = await new Promise((res) => canvas.current.toBlob(res, "image/jpeg", 0.88));
    const fd = new FormData();
    fd.append("file", blob, "annotated.jpg");
    try {
      const { data } = await api.put(`/inspections/${inspectionId}/photos/${photo.id}`, fd);
      onSaved(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark bg-ink border-line text-slate-100 max-w-3xl p-4" dir="rtl" data-testid="photo-annotator-dialog">
        <DialogHeader><DialogTitle className="text-start">رسم وملاحظات على الصورة</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} data-testid={`annotator-tool-${t.id}`}
              className={`h-11 px-3 rounded-lg border flex items-center gap-1.5 text-sm ${tool === t.id ? "bg-brand-red text-white border-brand-red" : "border-line"}`}>
              <t.icon size={16} />{t.label}
            </button>
          ))}
          {COLORS.map((c) => (
            <button key={c} onClick={() => setColor(c)} aria-label={c} data-testid={`annotator-color-${c.slice(1)}`}
              className={`h-9 w-9 rounded-full border-2 ${color === c ? "border-brand-sky scale-110" : "border-line"} transition-transform`} style={{ background: c }} />
          ))}
          <button onClick={() => setShapes((s) => s.slice(0, -1))} data-testid="annotator-undo"
            className="h-11 px-3 rounded-lg border border-line flex items-center gap-1.5 text-sm"><Undo2 size={16} />تراجع</button>
        </div>
        {tool === "text" && (
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="اكتب الملاحظة ثم اضغط على مكانها في الصورة"
            className="h-11 bg-panel border-line" data-testid="annotator-text-input" />
        )}
        <div className="relative max-h-[60vh] overflow-auto rounded-lg bg-black grid place-items-center">
          {loading && <Loader2 className="animate-spin text-brand-red absolute" />}
          <canvas ref={canvas} className="touch-none max-w-full max-h-[60vh] object-contain" data-testid="annotator-canvas"
            onPointerDown={down} onPointerMove={move} onPointerUp={() => (active.current = null)} onPointerLeave={() => (active.current = null)} />
        </div>
        <button onClick={save} disabled={saving || loading} data-testid="annotator-save-button"
          className="h-12 rounded-lg bg-brand-red text-white font-bold flex items-center justify-center gap-2 hover:bg-brand-red-dark disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} حفظ الصورة بالملاحظات
        </button>
      </DialogContent>
    </Dialog>
  );
};
